import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Tenant } from '../../tenant'
import type { User } from '../../user'
import type * as scope from '../scope'
import type { Statement } from '../statement'

/**
 * A shared, stored set of statements, assigned to many users. Editing a role
 * reaches every holder on their next request.
 *
 * A global role is editable by platform admins only; a tenant-owned role by
 * that tenant's administrators.
 */
export interface Role {
  /** @see {@link Role.Id} */
  readonly id: Role.Id
  readonly name: string
  readonly description?: string
  /**
   * The owning tenant, or `null` for a global role assignable in any tenant.
   * A tenant-owned role is assignable only within its own tenant
   */
  readonly tenantId: Tenant.Id | null
  /** Derived from {@link statements} on write, never supplied */
  readonly scopeContract: scope.Contract
  readonly statements: readonly Statement.Templated[]
  /** Epoch millis */
  readonly createdAt: number
  readonly createdBy: User.Id
  /** Epoch millis */
  readonly updatedAt: number
  readonly updatedBy: User.Id
}
export namespace Role {
  /** Identifies a {@link Role} */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.isString.usingAlias(
      'policy.grant.Role.Id',
    )
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /** Information needed to create a {@link Role} */
  export interface Input {
    readonly name: string
    readonly description?: string
    readonly tenantId: Tenant.Id | null
    readonly statements: readonly Statement.Input[]
  }

  /** Information used to update a {@link Role}. Ownership cannot change */
  export type Updates = Partial<Omit<Input, 'tenantId'>>

  /**
   * A pointer from a user to a {@link Role}, plus the values bound to the
   * role's `${scope.*}` variables. One user may hold the same role more than
   * once with different bindings.
   */
  export interface Assignment {
    /** @see {@link Assignment.Id} */
    readonly id: Assignment.Id
    readonly userId: User.Id
    readonly roleId: Role.Id
    /** @see {@link scope.Binding} */
    readonly scope: scope.Binding
    /** Epoch millis */
    readonly assignedAt: number
    readonly assignedBy: User.Id
  }
  export namespace Assignment {
    /** Identifies an {@link Assignment} */
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Role.Assignment.Id, beside Role.Id
    export type Id = string
    export namespace Id {
      export const isId: TypeChecker<Id> = TypeChecker.isString.usingAlias(
        'policy.grant.Role.Assignment.Id',
      )
      export const assertIsId: TypeAsserter<Id> =
        TypeAsserter.Factory.from(isId)
    }
  }
}
